import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { Bell, Check, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TYPE_ICONS = {
  info: "text-blue-500",
  success: "text-emerald-500",
  warning: "text-amber-500",
  error: "text-red-500",
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

export const NotificationBell = ({ collapsed }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {});
      fetchNotifications();
    } catch {
      // silent
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {});
      fetchNotifications();
    } catch {
      // silent
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className={`relative flex items-center gap-3 w-full rounded-lg p-2 mb-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-400 ${collapsed ? "justify-center" : ""}`}
        >
          <Bell size={20} />
          {!collapsed && <span className="text-sm font-medium">Notifications</span>}
          {unreadCount > 0 && (
            <span className={`absolute ${collapsed ? "top-0 right-0" : "top-1 left-5"} flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full`}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="right" className="w-80 max-h-96 overflow-y-auto p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              data-testid="mark-all-read"
            >
              <CheckCircle size={14} /> Tout marquer lu
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Aucune notification
          </div>
        ) : (
          <div>
            {notifications.map(notif => (
              <div
                key={notif.notification_id}
                className={`px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors ${
                  !notif.lu ? "bg-blue-50/50 dark:bg-blue-950/20" : ""
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 ${TYPE_ICONS[notif.type_notification] || "text-slate-400"}`}>
                    <Bell size={14} weight="fill" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{notif.titre}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{notif.message}</p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">{timeAgo(notif.created_at)}</p>
                  </div>
                  {!notif.lu && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markAsRead(notif.notification_id); }}
                      className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                      title="Marquer comme lu"
                    >
                      <Check size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
